<script>
  import MultiSelect from './multi_select.svelte'
  import { loadSelectOptions } from '../lib/shared_props.js'

  // Bindable value shape mirrors QuestTemplate's include/exclude targeting (quest_template_places,
  // _categories, _sub_categories, _excluded_places, _excluded_sub_categories — see README "Targeting
  // value shape"): { category_ids, sub_category_ids, places: [{ place_id, radius_m }],
  // excluded_sub_category_ids, excluded_place_ids }. `allowPlaceRadius` gives each specific place an
  // Override toggle (form-only `override_radius` on the entry) that reveals a radius input. Off: the
  // server takes the larger of the quest radius and the place's own Checkin Radius. On:
  // quest_template_places.radius_m is used as-is; blank falls back to `defaultRadiusM` on submit.
  let {
    value = $bindable(defaultTargeting()),
    categories = [],
    subCategories = [],
    placeSourceParams = {},
    allowPlaceRadius = false,
    defaultRadiusM = null,
    error = null
  } = $props()

  function defaultTargeting() {
    return { category_ids: [], sub_category_ids: [], places: [], excluded_sub_category_ids: [], excluded_place_ids: [] }
  }

  // Every change replaces the whole value: the bound parent is often a `useForm` store, whose
  // plain objects don't react to in-place property writes.
  const set = (key, next) => (value = { ...value, [key]: next })
  const updatePlace = (placeId, changes) =>
    set('places', value.places.map((entry) => (entry.place_id === placeId ? { ...entry, ...changes } : entry)))

  const placeIds = {
    get value() {
      return value.places.map((entry) => entry.place_id)
    },
    set value(ids) {
      set('places', ids.map((id) => value.places.find((entry) => entry.place_id === id) || { place_id: id, override_radius: false, radius_m: null }))
    }
  }

  // Toggling off clears the value too, so a later toggle-on starts from the quest radius
  // rather than a stale number.
  const setOverride = (placeId, on) =>
    updatePlace(placeId, { override_radius: on, radius_m: on && defaultRadiusM ? Number(defaultRadiusM) : null })

  const setRadius = (placeId, radius) => updatePlace(placeId, { radius_m: radius === '' ? null : Number(radius) })

  // Place pickers are server-backed (source="places" — searched + paginated); labels
  // for already-saved ids (the radius rows below) hydrate once via an ids lookup.
  let placeLabels = $state({})
  let hydratedPlaces = false
  $effect(() => {
    if (hydratedPlaces) return
    hydratedPlaces = true
    const ids = [
      ...new Set([...value.places.map((entry) => entry.place_id), ...value.excluded_place_ids])
    ].filter((id) => !placeLabels[id])
    if (ids.length === 0) return
    loadSelectOptions('places', { ids })
      .then((data) => data.options.forEach((option) => (placeLabels[option.value] = option.label)))
      .catch(() => {})
  })

  const rememberPlace = (option) => (placeLabels[option.value] = option.label)
  const placeLabel = (id) => placeLabels[id] || id
</script>

<div class="grid grid-cols-1 gap-6 sm:grid-cols-2">
  <div class="flex flex-col gap-4">
    <h3 class="font-semibold">Include</h3>
    <MultiSelect bind:value={() => value.category_ids, (next) => set('category_ids', next)} options={categories} label="Categories" />
    <MultiSelect bind:value={() => value.sub_category_ids, (next) => set('sub_category_ids', next)} options={subCategories} label="Sub-categories" />
    {#if allowPlaceRadius}
      <p class="text-xs text-base-content/60">
        Venues matched by category targeting always use the quest's default radius. To give one venue a custom
        radius, assign that place directly below.
      </p>
    {/if}
    <MultiSelect bind:value={placeIds.value} source="places" sourceParams={placeSourceParams} label="Specific places" onadd={rememberPlace} />

    {#if allowPlaceRadius && value.places.length > 0}
      <fieldset class="fieldset">
        <legend class="fieldset-legend">Geofence radius per place (meters)</legend>
        <p class="label whitespace-normal">
          Off: the larger of the quest's radius{defaultRadiusM ? ` (${defaultRadiusM}m)` : ''} and the place's own
          Checkin Radius. Override: the radius entered here is used as-is, even if the place's Checkin Radius is larger.
        </p>
        <div class="flex flex-col gap-2">
          {#each value.places as entry (entry.place_id)}
            <div class="flex items-center gap-3 min-h-8">
              <span class="text-sm flex-1">{placeLabel(entry.place_id)}</span>
              <label class="label whitespace-normal text-sm text-base-content gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  class="checkbox checkbox-sm"
                  checked={!!entry.override_radius}
                  onchange={(event) => setOverride(entry.place_id, event.target.checked)}
                />
                Override
              </label>
              {#if entry.override_radius}
                <input
                  type="number"
                  class="input input-sm w-28"
                  aria-label={`Radius for ${placeLabel(entry.place_id)}`}
                  placeholder={defaultRadiusM ? `${defaultRadiusM}` : 'default'}
                  value={entry.radius_m ?? ''}
                  oninput={(event) => setRadius(entry.place_id, event.target.value)}
                />
              {/if}
            </div>
          {/each}
        </div>
      </fieldset>
    {/if}
  </div>

  <div class="flex flex-col gap-4">
    <h3 class="font-semibold">Exclude</h3>
    <p class="text-xs text-base-content/60">Exclusions override inclusions.</p>
    <MultiSelect bind:value={() => value.excluded_sub_category_ids, (next) => set('excluded_sub_category_ids', next)} options={subCategories} label="Excluded sub-categories" />
    <MultiSelect bind:value={() => value.excluded_place_ids, (next) => set('excluded_place_ids', next)} source="places" sourceParams={placeSourceParams} label="Excluded places" onadd={rememberPlace} />
    {#if error}<p class="text-error text-sm">{error}</p>{/if}
  </div>
</div>
